/**
 * Questionnaires, from the admin.
 *
 * This is the last thing in the app that could only be written by editing PHP.
 * It is also the least form-shaped: a questionnaire is a set of dimensions, a
 * list of questions scored against them, and the two thresholds that turn a
 * score into one of three sentences. A flat form would have been a JSON box,
 * which is a way of saying "write it in PHP after all".
 *
 * So the editor is three stacked lists — dimensions, questions, bands — and the
 * refusals come back from the server with a sentence that names what is wrong,
 * because the rules that matter (a question scored against a dimension that
 * does not exist, two questions sharing an id, bands in the wrong order) are
 * the server's to enforce and not worth stating twice.
 */

import { apiGet, apiPost, apiPut, apiDelete, ApiError } from '../js/api.js'
import { text } from '../js/html.js'
import { adminHeader, mountAdminHeader } from '../components/adminShell.js'

const EMPTY = {
  title: '',
  kind: 'assessment',
  series: '',
  sequence: 0,
  opens_at_local: '',
  reveal_at_local: '',
  timezone: null,
  definition: { dimensions: {}, questions: [], bands: { low: 40, mid: 70 } },
}

export async function renderAdminQuestionnaires(root, { retreatId, onNavigate, onExit }) {
  root.innerHTML = `<div class="admin"><p class="lede">Loading…</p></div>`

  let data = await apiGet(`/admin/retreats/${retreatId}/questionnaires`)
  let editing = null // a working copy, never the row itself
  let error = null
  let notice = null

  async function reload() {
    data = await apiGet(`/admin/retreats/${retreatId}/questionnaires`)
  }

  function draw() {
    root.innerHTML = `
      <div class="admin">
        ${adminHeader({
          retreat: data.retreat,
          tab: 'questions',
          subtitle: `${data.questionnaires.length} ${data.questionnaires.length === 1 ? 'questionnaire' : 'questionnaires'}`,
        })}

        <div class="atoolbar">
          <button class="button button--primary admin__new" data-new>New questionnaire</button>
        </div>

        ${error ? `<p class="notice notice--error">${text(error)}</p>` : ''}
        ${notice ? `<p class="notice notice--info">${text(notice)}</p>` : ''}

        ${editing === null ? '' : editor(editing)}

        ${
          data.questionnaires.length === 0
            ? '<p class="lede">None yet. The scenario brings thirteen when you clone it.</p>'
            : `<table class="admin-table">
                 <thead>
                   <tr><th>Opens</th><th>Title</th><th>Kind</th><th>Questions</th><th>Answers</th><th></th></tr>
                 </thead>
                 <tbody>${data.questionnaires.map(row).join('')}</tbody>
               </table>`
        }
      </div>
    `

    mountAdminHeader(root, { onNavigate, onExit })
    wire()
  }

  function row(item) {
    const scales = (item.definition.questions ?? []).filter((q) => q.type === 'scale').length
    const texts = (item.definition.questions ?? []).length - scales

    return `
      <tr>
        <td class="admin-table__muted">
          ${text(item.opens_at_local?.replace('T', ' ') ?? '—')}
          ${item.series ? `<span class="admin-table__zone">${text(item.series)} #${item.sequence}</span>` : ''}
        </td>
        <td>${text(item.title)}</td>
        <td class="admin-table__muted"><span class="akind akind--${text(item.kind)}">${text(item.kind)}</span></td>
        <td class="admin-table__muted">${scales} scale${texts > 0 ? ` · ${texts} written` : ''}</td>
        <td class="admin-table__muted">${item.answer_count}</td>
        <td class="admin-table__actions">
          <button class="link-button" data-edit="${item.id}">Edit</button>
          <button class="link-button link-button--danger" data-delete="${item.id}">Delete</button>
        </td>
      </tr>
    `
  }

  // ---- the editor ---------------------------------------------------------

  function editor(working) {
    const dimensions = Object.entries(working.definition.dimensions ?? {})

    return `
      <section class="card admin-form" id="q-editor">
        <h2>${working.id ? `Edit: ${text(working.title)}` : 'New questionnaire'}</h2>

        <p class="admin-form__section">What it is</p>

        <label class="field">
          <span class="field__label">Title</span>
          <input class="field__input" data-q="title" value="${text(working.title)}" />
        </label>

        <div class="admin-form__grid">
          <label class="field">
            <span class="field__label">Kind</span>
            <select class="field__input" data-q="kind">
              ${data.kinds
                .map(
                  (kind) =>
                    `<option value="${text(kind)}" ${working.kind === kind ? 'selected' : ''}>${text(kind)}</option>`,
                )
                .join('')}
            </select>
            <span class="hint">A check-in is the same questions asked again, to compare.</span>
          </label>

          <label class="field">
            <span class="field__label">Series</span>
            <input class="field__input" data-q="series" value="${text(working.series)}"
                   placeholder="checkin" />
            <span class="hint">Check-ins of one series are plotted against each other.</span>
          </label>

          <label class="field">
            <span class="field__label">Place in the series</span>
            <input class="field__input" type="number" min="0" data-q="sequence" value="${working.sequence ?? 0}" />
          </label>
        </div>

        <p class="admin-form__section">When</p>

        <div class="admin-form__grid">
          <label class="field">
            <span class="field__label">Opens</span>
            <input class="field__input" type="datetime-local" data-q="opens_at_local"
                   value="${text(working.opens_at_local)}" />
          </label>

          <label class="field">
            <span class="field__label">Result appears</span>
            <input class="field__input" type="datetime-local" data-q="reveal_at_local"
                   value="${text(working.reveal_at_local)}" />
            <span class="hint">Before this the result is never computed, not merely hidden.</span>
          </label>

          <label class="field">
            <span class="field__label">Timezone</span>
            <select class="field__input" data-q="timezone">
              <option value="">${text(data.retreat.timezone)} (retreat)</option>
              ${data.timezones
                .map(
                  (zone) =>
                    `<option value="${text(zone)}" ${working.timezone === zone ? 'selected' : ''}>${text(zone)}</option>`,
                )
                .join('')}
            </select>
          </label>
        </div>

        <p class="admin-form__section">What it measures</p>
        <p class="hint">
          Every scale question is scored into one of these, and the score picks
          one of its three sentences. A dimension with no questions never
          appears in a result.
        </p>

        ${dimensions.map(([key, dimension]) => dimensionBlock(key, dimension)).join('')}
        ${dimensions.length === 0 ? '<p class="lede">No dimensions yet.</p>' : ''}

        <div class="admin-form__actions">
          <button class="button button--ghost" type="button" data-add-dimension>Add a dimension</button>
        </div>

        <p class="admin-form__section">The questions</p>

        ${(working.definition.questions ?? []).map((question, index) => questionBlock(question, index, dimensions)).join('')}
        ${(working.definition.questions ?? []).length === 0 ? '<p class="lede">No questions yet.</p>' : ''}

        <div class="admin-form__actions">
          <button class="button button--ghost" type="button" data-add-question="scale">Add a scale</button>
          <button class="button button--ghost" type="button" data-add-question="text">Add a written answer</button>
        </div>

        <p class="admin-form__section">Where the bands fall</p>
        <p class="hint">
          Scores are normalised to 0–100. Below the first number reads as “low”,
          below the second as “mid”, above it as “high”.
        </p>

        <div class="admin-form__grid">
          <label class="field">
            <span class="field__label">Low below</span>
            <input class="field__input" type="number" min="1" max="99" data-band="low"
                   value="${working.definition.bands?.low ?? 40}" />
          </label>

          <label class="field">
            <span class="field__label">Mid below</span>
            <input class="field__input" type="number" min="1" max="99" data-band="mid"
                   value="${working.definition.bands?.mid ?? 70}" />
          </label>
        </div>

        <div class="admin-form__actions">
          <button class="button button--primary" type="button" data-save>Save</button>
          <button class="button button--ghost" type="button" data-cancel>Cancel</button>
        </div>
      </section>
    `
  }

  function dimensionBlock(key, dimension) {
    return `
      <section class="qblock" data-dimension="${text(key)}">
        <p class="qblock__head">
          <span class="qblock__tag">${text(key)}</span>
          <button class="link-button link-button--danger" data-drop-dimension="${text(key)}">Remove</button>
        </p>

        <label class="field">
          <span class="field__label">Name</span>
          <input class="field__input" data-dim-field="label" value="${text(dimension.label)}" />
        </label>

        <div class="admin-form__grid">
          ${['low', 'mid', 'high']
            .map(
              (band) => `
            <label class="field">
              <span class="field__label">If ${band}</span>
              <textarea class="field__input" rows="3" data-dim-interpretation="${band}">${text(
                dimension.interpretation?.[band],
              )}</textarea>
            </label>
          `,
            )
            .join('')}
        </div>
      </section>
    `
  }

  function questionBlock(question, index, dimensions) {
    const isScale = question.type === 'scale'

    return `
      <section class="qblock qblock--${isScale ? 'scale' : 'text'}" data-question="${index}">
        <p class="qblock__head">
          <span class="qblock__tag">${index + 1} · ${isScale ? 'scale' : 'written'}</span>
          <span class="qblock__id">${text(question.id)}</span>
          <button class="link-button" data-move="${index}:-1" ${index === 0 ? 'disabled' : ''}>↑</button>
          <button class="link-button" data-move="${index}:1">↓</button>
          <button class="link-button link-button--danger" data-drop-question="${index}">Remove</button>
        </p>

        <label class="field">
          <span class="field__label">Question</span>
          <input class="field__input" data-q-field="text" value="${text(question.text)}" />
        </label>

        ${
          isScale
            ? `<div class="admin-form__grid">
                 <label class="field">
                   <span class="field__label">Scored into</span>
                   <select class="field__input" data-q-field="dimension">
                     <option value="">—</option>
                     ${dimensions
                       .map(
                         ([key, dimension]) =>
                           `<option value="${text(key)}" ${question.dimension === key ? 'selected' : ''}>${text(dimension.label || key)}</option>`,
                       )
                       .join('')}
                   </select>
                 </label>

                 <label class="field">
                   <span class="field__label">From</span>
                   <input class="field__input" type="number" data-q-field="min" value="${question.min ?? 1}" />
                 </label>

                 <label class="field">
                   <span class="field__label">To</span>
                   <input class="field__input" type="number" data-q-field="max" value="${question.max ?? 7}" />
                 </label>

                 <label class="field">
                   <span class="field__label">Label at the bottom</span>
                   <input class="field__input" data-q-field="min_label" value="${text(question.min_label)}" />
                 </label>

                 <label class="field">
                   <span class="field__label">Label at the top</span>
                   <input class="field__input" data-q-field="max_label" value="${text(question.max_label)}" />
                 </label>
               </div>

               <label class="checkbox">
                 <input type="checkbox" data-q-field="reverse" ${question.reverse ? 'checked' : ''} />
                 <span>Reverse it — a high answer means a low score.</span>
               </label>`
            : `<label class="checkbox">
                 <input type="checkbox" data-q-field="optional" ${question.optional ? 'checked' : ''} />
                 <span>Optional — it can be left empty.</span>
               </label>`
        }
      </section>
    `
  }

  // ---- behaviour ----------------------------------------------------------

  /**
   * Reads the whole editor back out of the DOM.
   *
   * Every keystroke could be written into `editing` instead, but redrawing on
   * input would move the caret, and not redrawing would leave two copies of the
   * truth. Reading it once, when something structural happens, keeps one.
   */
  function collect() {
    const form = root.querySelector('#q-editor')

    if (form === null) return editing

    const value = (name) => form.querySelector(`[data-q="${name}"]`)?.value ?? ''

    const dimensions = {}

    form.querySelectorAll('[data-dimension]').forEach((node) => {
      dimensions[node.dataset.dimension] = {
        label: node.querySelector('[data-dim-field="label"]').value,
        interpretation: Object.fromEntries(
          ['low', 'mid', 'high'].map((band) => [
            band,
            node.querySelector(`[data-dim-interpretation="${band}"]`).value,
          ]),
        ),
      }
    })

    const questions = [...form.querySelectorAll('[data-question]')].map((node, index) => {
      const field = (name) => node.querySelector(`[data-q-field="${name}"]`)
      const previous = editing.definition.questions[Number(node.dataset.question)] ?? {}
      const isScale = node.classList.contains('qblock--scale')

      const built = {
        id: previous.id || `q${index + 1}`,
        type: isScale ? 'scale' : 'text',
        text: field('text').value,
      }

      if (isScale) {
        built.dimension = field('dimension').value
        built.min = Number(field('min').value)
        built.max = Number(field('max').value)
        built.min_label = field('min_label').value
        built.max_label = field('max_label').value
        built.reverse = field('reverse').checked
      } else {
        built.optional = field('optional').checked
      }

      return built
    })

    return {
      ...editing,
      title: value('title'),
      kind: value('kind'),
      series: value('series'),
      sequence: Number(value('sequence')) || 0,
      opens_at_local: value('opens_at_local'),
      reveal_at_local: value('reveal_at_local'),
      timezone: value('timezone') || null,
      definition: {
        dimensions,
        questions,
        bands: {
          low: Number(form.querySelector('[data-band="low"]').value),
          mid: Number(form.querySelector('[data-band="mid"]').value),
        },
      },
    }
  }

  /** Reads the editor, changes it, and redraws from the result. */
  function change(mutate) {
    editing = collect()
    mutate(editing)
    error = null
    draw()
  }

  function wire() {
    root.querySelector('[data-new]')?.addEventListener('click', () => {
      editing = structuredClone(EMPTY)
      error = null
      notice = null
      draw()
    })

    root.querySelectorAll('[data-edit]').forEach((button) =>
      button.addEventListener('click', () => {
        const found = data.questionnaires.find((item) => item.id === Number(button.dataset.edit))

        editing = structuredClone({ ...EMPTY, ...found })
        editing.definition = { ...EMPTY.definition, ...(found.definition ?? {}) }
        error = null
        notice = null
        draw()
      }),
    )

    root.querySelectorAll('[data-delete]').forEach((button) =>
      button.addEventListener('click', async () => {
        if (!window.confirm('Delete this questionnaire?')) return

        await act(() => apiDelete(`/admin/questionnaires/${button.dataset.delete}`))
      }),
    )

    root.querySelector('[data-add-dimension]')?.addEventListener('click', () => {
      const key = window.prompt('A short key for the dimension, in English: energy, boundaries, direction…')

      if (key === null || key.trim() === '') return

      change((working) => {
        working.definition.dimensions[key.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_')] = {
          label: key.trim(),
          interpretation: { low: '', mid: '', high: '' },
        }
      })
    })

    root.querySelectorAll('[data-drop-dimension]').forEach((button) =>
      button.addEventListener('click', () =>
        change((working) => {
          delete working.definition.dimensions[button.dataset.dropDimension]
        }),
      ),
    )

    root.querySelectorAll('[data-add-question]').forEach((button) =>
      button.addEventListener('click', () =>
        change((working) => {
          const type = button.dataset.addQuestion
          const next = working.definition.questions.length + 1

          working.definition.questions.push(
            type === 'scale'
              ? {
                  id: `q${next}`,
                  type: 'scale',
                  text: '',
                  dimension: Object.keys(working.definition.dimensions)[0] ?? '',
                  min: 1,
                  max: 7,
                  min_label: '',
                  max_label: '',
                }
              : { id: `q${next}`, type: 'text', text: '' },
          )
        }),
      ),
    )

    root.querySelectorAll('[data-drop-question]').forEach((button) =>
      button.addEventListener('click', () =>
        change((working) => {
          working.definition.questions.splice(Number(button.dataset.dropQuestion), 1)
        }),
      ),
    )

    root.querySelectorAll('[data-move]').forEach((button) =>
      button.addEventListener('click', () =>
        change((working) => {
          const [from, step] = button.dataset.move.split(':').map(Number)
          const to = from + step
          const list = working.definition.questions

          if (to < 0 || to >= list.length) return

          ;[list[from], list[to]] = [list[to], list[from]]
        }),
      ),
    )

    root.querySelector('[data-save]')?.addEventListener('click', save)
    root.querySelector('[data-cancel]')?.addEventListener('click', () => {
      editing = null
      error = null
      draw()
    })
  }

  async function save() {
    const working = collect()

    await act(async () => {
      if (working.id) {
        await apiPut(`/admin/questionnaires/${working.id}`, working)
      } else {
        await apiPost(`/admin/retreats/${retreatId}/questionnaires`, working)
      }

      notice = 'Saved.'
      editing = null
    })
  }

  /**
   * Runs a mutation and keeps the editor open when it fails.
   *
   * A refusal here names something the person typed — a question scored into a
   * dimension that does not exist, two questions with one id — so throwing the
   * form away with it would be throwing away the thing they need to fix.
   */
  async function act(work) {
    try {
      error = null
      await work()
      await reload()
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'That did not work.'
      notice = null
    }

    draw()
  }

  draw()
}
